import { BoatPreset, cannonStatsAt, CannonType, deriveBoatTree, EmblemFitting, Spread } from "../../../shared/BoatTree";
import { BOAT } from "../../../shared/Config";
import { rigDrive } from "../../../shared/Fitout";
import { hexOf, hslOf, packColor, packHsl } from "../../../shared/Settings";
import { EquippedBoat, PlayerStore, StoredPlayerData } from "../data/Player";
import { boatCard, fittingCard } from "./Cards";

/**
 * The shop: where a captain spends what a battle paid them.
 *
 * **Chrome over the live attract sea.** By design/UI.md's dividing question nothing in here has a
 * position in the sea, so all of it is real DOM — tabs, cards, prices, focus and keyboard order
 * for free. `MainScene` keeps sailing behind it rather than being torn down, which is what makes
 * equipping a boat visible on something bigger than a card: the change lands on a whole ship under
 * way.
 *
 * **The pictures are Pixi**, built with the game's own `Deck` and lifted into a `<canvas>` — see
 * [Cards.ts](Cards.ts) for why that rather than cropping the atlas.
 *
 * **Handling is bars, not numbers.** A `turn` of 0.84 means everything to the dashboard and
 * nothing to a player; three bars drawn against the fastest and slowest boat in the catalogue can
 * be compared at a glance by somebody who has never seen a multiplier. The figures belong in the
 * Boat panel.
 *
 * It knows nothing about arenas, wallets or storage beyond the `PlayerStore` it is handed — the
 * same split the toolbar has, and the reason a server-backed store can replace the local one
 * without this file changing. See design/Shop.md.
 */

/** Which tab is open. */
type Tab = "boats" | "cannons" | "colours";

/** Which end of the ship a cannon card is buying for. */
type Slot = 0 | 1;

const SLOT_LABELS = ["Bow", "Stern"] as const;

export class Shop {
    private readonly _root = document.createElement("div");
    private readonly _tabs = document.createElement("div");
    private readonly _body = document.createElement("div");
    private readonly _wallet = document.createElement("span");
    private readonly _tabButtons = new Map<Tab, HTMLButtonElement>();

    private _open: Tab = "boats";
    private _data: StoredPlayerData | null = null;

    /**
     * The longest boat in the catalogue, so every card in the gallery is drawn against one scale
     * and the row reads as a shelf of different ships rather than the same ship repeated.
     */
    private readonly _longest = Math.max(1, ...BOAT.presets.map((preset) => preset.length));
    private readonly _derived = deriveBoatTree(BOAT);

    /**
     * Called when the locker changes — a purchase, or a different boat equipped. Whoever owns the
     * screen redraws the attract sea from it; the shop has no idea there is one.
     */
    public onChanged: (() => void) | null = null;

    /** Called when the panel comes down, however it came down. */
    public onClosed: (() => void) | null = null;

    /** Whether the panel is up. Closing it does not tear it down: it is opened and shut, not built. */
    public get visible(): boolean {
        return !this._root.hidden;
    }

    constructor(private readonly _store: PlayerStore) {
        this._root.className = "shop";
        this._root.hidden = true;

        const head = document.createElement("div");
        head.className = "shop-head";

        this._tabs.className = "shop-tabs";
        this._tabs.appendChild(this._buildTab("boats", "Boats"));
        this._tabs.appendChild(this._buildTab("cannons", "Cannons"));
        this._tabs.appendChild(this._buildTab("colours", "Colours"));

        this._wallet.className = "shop-wallet";

        const close = document.createElement("button");
        close.className = "shop-close";
        close.type = "button";
        close.textContent = "Done";
        close.addEventListener("click", () => this.close());

        head.append(this._tabs, this._wallet, close);

        this._body.className = "shop-body";
        this._root.append(head, this._body);
    }

    public mount(parent: HTMLElement = document.body): void {
        parent.appendChild(this._root);
    }

    public destroy(): void {
        this._root.remove();
    }

    /** Reads the locker fresh and draws. Async because the store is, and will be more so. */
    public async open(): Promise<void> {
        this._data = await this._store.load();
        this._root.hidden = false;
        this._render();
    }

    public close(): void {
        if (this._root.hidden) return;

        this._root.hidden = true;
        this.onClosed?.();
    }

    private _buildTab(tab: Tab, label: string): HTMLButtonElement {
        const button = document.createElement("button");
        button.className = "shop-tab";
        button.type = "button";
        button.textContent = label;
        button.addEventListener("click", () => {
            this._open = tab;
            this._render();
        });

        this._tabButtons.set(tab, button);
        return button;
    }

    /**
     * Rebuilds the open tab from scratch.
     *
     * Wholesale rather than patched, and it is affordable because it only ever runs on a click: a
     * tab change, a purchase, an equip. Every card carries an extracted `<canvas>`, so a render
     * that happened per frame would be renderer work per frame — but a shop is a thing somebody
     * clicks a handful of times, and the alternative is diffing a grid to save nothing.
     */
    private _render(): void {
        const data = this._data;
        if (!data) return;

        this._wallet.textContent = `${data.wallet.doubloons.toLocaleString()} dbl`;
        for (const [tab, button] of this._tabButtons) button.classList.toggle("is-open", tab === this._open);

        this._body.replaceChildren();
        if (this._open === "boats") this._renderBoats(data);
        else if (this._open === "cannons") this._renderCannons(data);
        else this._renderColours(data);
    }

    /** One card per boat on sale, drawn as the ship it actually is. */
    private _renderBoats(data: StoredPlayerData): void {
        const gallery = document.createElement("div");
        gallery.className = "shop-gallery";

        BOAT.presets.forEach((preset, index) => {
            gallery.appendChild(this._boatCard(data, preset, index));
        });

        this._body.append(gallery, note("Speed, turning and canvas are fixed for a whole life — a boat is a decision, not a starting point."));
    }

    private _boatCard(data: StoredPlayerData, preset: BoatPreset, index: number): HTMLElement {
        const owned = data.locker.presets.includes(index);
        const equipped = data.locker.equipped.preset === index;
        const card = shell(owned, equipped, data.wallet.doubloons >= preset.cost);

        // Drawn with the guns this captain has actually equipped **and in their own colours**, so a
        // boat card is a picture of *their* ship rather than of a catalogue entry. A gallery in
        // somebody else's colours would be the shop quietly disagreeing with the Colours tab.
        const art = boatCard({ preset: index, ...loadoutColours(data.locker.equipped) }, this._longest);
        if (art) card.appendChild(art);

        card.appendChild(price(index === 0 ? 0 : preset.cost, owned));

        const bars = document.createElement("div");
        bars.className = "shop-bars";
        bars.append(
            bar("Speed", preset.speed, this._derived.speed),
            bar("Turning", preset.turn, this._derived.turn),
            bar("Canvas", rigDrive(preset.rig), this._derived.drive)
        );
        card.appendChild(bars);

        card.appendChild(
            this._action(equipped, owned, preset.cost, async () => {
                if (!owned && !(await this._store.spend({ kind: "preset", index, cost: preset.cost }))) return;
                await this._store.equip({ ...data.locker.equipped, preset: index });
            })
        );

        return card;
    }

    /**
     * Two columns, bow and stern, because that is the choice: a captain picks each end
     * independently and a long gun forward with a carronade aft is a real build.
     */
    private _renderCannons(data: StoredPlayerData): void {
        const columns = document.createElement("div");
        columns.className = "shop-columns";

        for (const slot of [0, 1] as Slot[]) {
            const column = document.createElement("div");
            column.className = "shop-column";

            const heading = document.createElement("h3");
            heading.textContent = SLOT_LABELS[slot];
            column.appendChild(heading);

            BOAT.cannons.forEach((cannon, index) => column.appendChild(this._cannonCard(data, cannon, index, slot)));
            columns.appendChild(column);
        }

        this._body.append(
            columns,
            note("A gun is bought once and fits either end. Both of yours level up together as you climb, on the curve their own type was authored with.")
        );
    }

    private _cannonCard(data: StoredPlayerData, cannon: CannonType, index: number, slot: Slot): HTMLElement {
        const owned = data.locker.cannons.includes(index);
        const equipped = data.locker.equipped.cannons[slot] === index;
        const card = shell(owned, equipped, data.wallet.doubloons >= cannon.cost);
        card.classList.add("is-gun");

        const art = fittingCard(cannon.frame);
        if (art) card.appendChild(art);

        card.appendChild(price(index === 0 ? 0 : cannon.cost, owned));

        // What a captain will actually put to sea with, which is level 1 — not what it grows into.
        const stats = cannonStatsAt(cannon, 1);
        const figures = document.createElement("div");
        figures.className = "shop-figures";
        figures.append(
            figure(`${Math.round(stats.steal * 100)}%`, "of a hold"),
            figure(Math.round(stats.range).toLocaleString(), "px reach"),
            figure(`${(stats.cooldown / 1000).toFixed(1)}s`, "reload")
        );
        card.appendChild(figures);

        card.appendChild(
            this._action(equipped, owned, cannon.cost, async () => {
                if (!owned && !(await this._store.spend({ kind: "cannon", index, cost: cannon.cost }))) return;

                const cannons = [...data.locker.equipped.cannons];
                cannons[slot] = index;
                await this._store.equip({ ...data.locker.equipped, cannons });
            })
        );

        return card;
    }

    /**
     * Three choices, and none of them costs anything.
     *
     * **The preview is the attract boat**, not a swatch. A sail is judged at the size it is
     * actually seen from, on the water it is actually seen against — the same reason the shop is an
     * overlay rather than a screen of its own. So nothing in here draws a boat: every change is
     * written to the locker, and `onChanged` puts it on the ship already sailing behind the panel.
     */
    private _renderColours(data: StoredPlayerData): void {
        const columns = document.createElement("div");
        columns.className = "shop-columns";
        columns.append(this._sailColumn(data), this._hullColumn(data), this._emblemColumn(data));

        this._body.append(
            columns,
            note("Your colours cost nothing and are yours from the first battle — a captain should be recognisable before they are rich. The sail has a floor on how dark it goes, so a ship can still be read against the water.")
        );
    }

    /** Free colour, with a floor. See `pickerColumn`. */
    private _sailColumn(data: StoredPlayerData): HTMLElement {
        return pickerColumn("Sail", data.locker.equipped.sailTint, BOAT.cosmetics.sailLightnessFloor, (hex) =>
            this._equip(data, { sailTint: hex })
        );
    }

    /**
     * The woods, as **one strip in the order the catalogue lists them** — light to dark. A grid
     * would say these are unrelated options; they are one axis, and a ramp is what an axis looks
     * like.
     */
    private _hullColumn(data: StoredPlayerData): HTMLElement {
        const column = section("Hull");

        const ramp = document.createElement("div");
        ramp.className = "shop-ramp";

        const picked = new Map<string, HTMLButtonElement>();
        for (const wood of BOAT.cosmetics.woods) {
            const swatch = document.createElement("button");
            swatch.type = "button";
            swatch.className = "shop-wood";
            swatch.style.background = wood;
            swatch.title = wood;
            swatch.setAttribute("aria-label", `Hull wood ${wood}`);
            swatch.classList.toggle("is-picked", wood === data.locker.equipped.hullTint);
            swatch.addEventListener("click", () => {
                for (const [, other] of picked) other.classList.remove("is-picked");
                swatch.classList.add("is-picked");
                void this._equip(data, { hullTint: wood });
            });

            picked.set(wood, swatch);
            ramp.appendChild(swatch);
        }

        column.appendChild(ramp);
        return column;
    }

    /** The devices, each drawn at its own art in the colour it will fly, and that colour. */
    private _emblemColumn(data: StoredPlayerData): HTMLElement {
        const column = section("Emblem");

        const row = document.createElement("div");
        row.className = "shop-devices";

        // A device card is an extracted canvas, so its colour is baked into pixels rather than set
        // on a sprite — changing the device colour means drawing the row again. It is a couple of
        // extractions on the end of a drag, which is what `change` rather than `input` buys.
        const drawDevices = () => {
            const buttons: HTMLButtonElement[] = [];
            BOAT.cosmetics.emblems.forEach((emblem, index) => {
                const button = this._deviceButton(data, emblem, index, () => {
                    for (const other of buttons) other.classList.remove("is-picked");
                    buttons[index].classList.add("is-picked");
                });
                buttons.push(button);
            });
            row.replaceChildren(...buttons);
        };
        drawDevices();

        column.appendChild(row);
        // The device's own colour, under the row of devices it applies to. No floor: a black
        // device on a pale sail is legible, and it is the sail that has to be read against water.
        column.appendChild(
            pickerControl(data.locker.equipped.emblemTint, 0, async (hex) => {
                await this._equip(data, { emblemTint: hex });
                drawDevices();
            })
        );

        return column;
    }

    private _deviceButton(data: StoredPlayerData, emblem: EmblemFitting, index: number, onPick: () => void): HTMLButtonElement {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "shop-device";
        button.classList.toggle("is-picked", index === data.locker.equipped.emblem);

        // Entry 0 has no art on purpose — it is the device that is no device — so it says so.
        const art = emblem.frame.length > 0 ? fittingCard(emblem.frame, packColor(data.locker.equipped.emblemTint)) : null;
        if (art) button.appendChild(art);
        else {
            const none = document.createElement("span");
            none.textContent = "None";
            button.appendChild(none);
        }

        button.addEventListener("click", () => {
            onPick();
            void this._equip(data, { emblem: index });
        });

        return button;
    }

    /**
     * Writes one cosmetic change and lets the sea show it.
     *
     * **No re-render.** Every other action in this shop rebuilds its tab, which is right for a
     * purchase — the wallet moved and four cards changed state. A colour changes one thing on a
     * boat that is not in this panel, and rebuilding would take the focus out of the slider being
     * dragged. The selected classes are toggled by hand at the call sites for the same reason.
     */
    private async _equip(data: StoredPlayerData, change: Partial<EquippedBoat>): Promise<void> {
        await this._store.equip({ ...data.locker.equipped, ...change });
        this._data = await this._store.load();
        // The panel is holding a stale copy; the next click has to build on this one.
        Object.assign(data.locker.equipped, this._data.locker.equipped);
        this.onChanged?.();
    }

    /**
     * The one button on a card, and what it says is the whole state machine: **Equipped** for the
     * thing already in use, **Equip** for something owned, and a price for something not.
     *
     * A card that is too expensive keeps its button and has it disabled rather than losing it. A
     * shop whose expensive things are unclickable is honest; one whose expensive things are
     * invisible reads as a short list.
     *
     * No confirm step and no undo, deliberately: the prices are small, and a modal over an attract
     * screen to ask "are you sure" about four hundred doubloons is worse than the mistake.
     */
    private _action(equipped: boolean, owned: boolean, cost: number, act: () => Promise<void>): HTMLButtonElement {
        const button = document.createElement("button");
        button.className = "shop-action";
        button.type = "button";

        const affordable = owned || (this._data?.wallet.doubloons ?? 0) >= cost;
        button.textContent = equipped ? "Equipped" : owned ? "Equip" : `Buy · ${cost.toLocaleString()}`;
        button.disabled = equipped || !affordable;
        // The action red is "this starts a fight" everywhere else in the game, and spending is the
        // one commitment on this screen — so it is the only thing here that wears it.
        button.classList.toggle("is-buy", !owned && affordable);

        button.addEventListener("click", () => {
            void act().then(async () => {
                // Re-read rather than mutating the copy in hand: the store is the truth about what
                // is owned, and on a server it is the only thing that can be.
                this._data = await this._store.load();
                this._render();
                this.onChanged?.();
            });
        });

        return button;
    }
}

/** A card, wearing whichever of the four states it is in. */
function shell(owned: boolean, equipped: boolean, affordable: boolean): HTMLElement {
    const card = document.createElement("div");
    card.className = "shop-card";
    card.classList.toggle("is-owned", owned && !equipped);
    card.classList.toggle("is-equipped", equipped);
    card.classList.toggle("is-dear", !owned && !affordable);

    return card;
}

/** What a thing costs, or that it is already yours. The free entries say so rather than "0". */
function price(cost: number, owned: boolean): HTMLElement {
    const label = document.createElement("strong");
    label.className = "shop-price";
    label.textContent = owned ? (cost === 0 ? "Free" : "Owned") : `${cost.toLocaleString()} dbl`;

    return label;
}

/**
 * One handling bar, drawn against the whole catalogue's range.
 *
 * A boat at the bottom of the range still shows a sliver rather than nothing: an empty bar reads
 * as "no data" where the truth is "the slowest one", and the slowest boat in the game is still a
 * boat that moves.
 */
function bar(label: string, value: number, range: Spread): HTMLElement {
    const row = document.createElement("div");
    row.className = "shop-bar";

    const name = document.createElement("span");
    name.textContent = label;

    const track = document.createElement("span");
    track.className = "shop-bar-track";

    const fill = document.createElement("span");
    fill.className = "shop-bar-fill";
    const span = range.max - range.min;
    fill.style.transform = `scaleX(${0.12 + 0.88 * (span > 0 ? (value - range.min) / span : 1)})`;
    track.appendChild(fill);

    row.append(name, track);
    return row;
}

/** One number on a gun card, with the unit under it. */
function figure(value: string, unit: string): HTMLElement {
    const cell = document.createElement("div");
    cell.className = "shop-figure";

    const number = document.createElement("strong");
    number.textContent = value;

    const caption = document.createElement("span");
    caption.textContent = unit;

    cell.append(number, caption);
    return cell;
}

/** A stored boat's guns and colours, as the `Loadout` fields a card is drawn from. */
function loadoutColours(equipped: EquippedBoat) {
    return {
        cannons: equipped.cannons,
        sailTint: packColor(equipped.sailTint),
        hullTint: packColor(equipped.hullTint),
        emblem: equipped.emblem,
        emblemTint: packColor(equipped.emblemTint)
    };
}

/** One labelled column of the Colours tab. */
function section(heading: string): HTMLElement {
    const column = document.createElement("div");
    column.className = "shop-column";

    if (heading.length > 0) {
        const title = document.createElement("h3");
        title.textContent = heading;
        column.appendChild(title);
    }

    return column;
}

/**
 * A colour, picked on the three axes a person actually thinks in: hue, how strong, how light.
 *
 * **Not a swatch row**, because free colour is the point — a captain who can only be one of six
 * reds is picking from a palette, which is the thing this replaced. And not `<input type="color">`
 * either: the browser's picker is a fine control and it would happily offer black, where the whole
 * job here is that **an unreachable colour is never offered**. `floor` is the lightness slider's
 * `min`, so the arena's clamp (design/Networking.md) never has anything to correct — a guard the
 * player meets as "this slider stops here" rather than as a colour that changed after they chose
 * it.
 *
 * The tracks are painted with the colours they select, so the control shows what it does rather
 * than three abstract numbers.
 *
 * Saved on `change` and not on `input`: a slider dragged across its width fires a hundred times,
 * and each one is a write to the locker and a rebuilt ship. The swatch follows the drag live, so
 * nothing feels latched.
 */
function pickerColumn(heading: string, value: string, floor: number, onPick: (hex: string) => void | Promise<void>): HTMLElement {
    const column = section(heading);
    column.appendChild(pickerControl(value, floor, onPick));

    return column;
}

/** The control itself: a swatch, its hex, and the three axes. See `pickerColumn`. */
function pickerControl(value: string, floor: number, onPick: (hex: string) => void | Promise<void>): HTMLElement {
    const wrap = document.createElement("div");
    const picker = document.createElement("div");
    picker.className = "shop-picker";

    const start = hslOf(packColor(value));
    const state = { h: start.h, s: start.s, l: start.l };

    const swatch = document.createElement("div");
    swatch.className = "shop-swatch";

    const readout = document.createElement("span");
    readout.className = "shop-hex";

    const rows: { row: HTMLElement; paint: () => void }[] = [];
    const draw = () => {
        const hex = hexOf(packHsl(state));
        swatch.style.background = hex;
        readout.textContent = hex;
        for (const { paint } of rows) paint();
        return hex;
    };

    const axis = (label: string, min: number, max: number, step: number, get: () => number, set: (raw: number) => void, track: () => string) => {
        const row = document.createElement("label");
        row.className = "shop-axis";

        const name = document.createElement("span");
        name.textContent = label;

        const slider = document.createElement("input");
        slider.type = "range";
        slider.min = String(min);
        slider.max = String(max);
        slider.step = String(step);
        slider.value = String(get());
        slider.addEventListener("input", () => {
            set(Number(slider.value));
            draw();
        });
        slider.addEventListener("change", () => void onPick(draw()));

        row.append(name, slider);
        rows.push({ row, paint: () => (slider.style.background = track()) });
        return row;
    };

    const stops = (count: number, at: (t: number) => string) =>
        `linear-gradient(90deg, ${Array.from({ length: count }, (_, i) => at(i / (count - 1))).join(", ")})`;

    picker.append(
        axis("Hue", 0, 359, 1, () => Math.round(state.h), (raw) => (state.h = raw), () =>
            stops(13, (t) => hexOf(packHsl({ h: t * 360, s: Math.max(0.35, state.s), l: Math.max(floor, 0.5) })))
        ),
        axis("Strength", 0, 100, 1, () => Math.round(state.s * 100), (raw) => (state.s = raw / 100), () =>
            stops(2, (t) => hexOf(packHsl({ h: state.h, s: t, l: state.l })))
        ),
        // The floor is the slider's own minimum, which is what "never offered" means.
        axis("Light", Math.ceil(floor * 100), 100, 1, () => Math.round(state.l * 100), (raw) => (state.l = raw / 100), () =>
            stops(9, (t) => hexOf(packHsl({ h: state.h, s: state.s, l: floor + t * (1 - floor) })))
        )
    );

    const head = document.createElement("div");
    head.className = "shop-picker-head";
    head.append(swatch, readout);

    wrap.append(head, picker);
    draw();

    return wrap;
}

/** A line under a gallery saying the thing the cards cannot. */
function note(text: string): HTMLElement {
    const line = document.createElement("p");
    line.className = "shop-note";
    line.textContent = text;

    return line;
}
